import { Tooltip } from 'components';
import { noop } from 'lodash/fp';
import { bool, func, number, shape, string } from 'prop-types';
import React, { MutableRefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoIconGreen } from 'static';

import { SearchIcon } from '../../../static';
import { StyledIconsBlock, StyledLeftIcon } from '../SearchInput/styled';
import { StyledErrorMessage, StyledLabel, StyledWrapper } from '../styled';
import {
  HidePasswordIcon,
  InputWrapper,
  LabelContainer,
  LabelWrapper,
  ShowPasswordButton,
  ShowPasswordIcon,
  StyledInfoIcon,
  StyledInput,
} from './styled';

const TextInput = ({
  name,
  type,
  min = null,
  pattern = null,
  placeholder,
  disabled,
  readonly = false,
  label,
  error,
  register,
  width,
  value,
  onChange,
  onBlur = () => {},
  defaultValue,
  withShowIcon,
  extraWrapperStyle,
  inputValueStyle,
  withSearchIcon = false,
  showInfoIcon = false,
  infoText = '',
  count = undefined,
  maxCount = undefined,
  countFontSize = 10,
  countFontWeight = 400,
}) => {
  const { t } = useTranslation();
  const [inputType, setType] = useState(type);

  const togglePasswordVisibility = () =>
    inputType === 'text' ? setType('password') : setType('text');

  const showCount = maxCount != null;
  const LabelRow = showCount ? LabelWrapper : LabelContainer;

  return (
    <StyledWrapper>
      <LabelRow>
        {label && (
          <StyledLabel htmlFor={name} error={error}>
            {label}
          </StyledLabel>
        )}
        {showCount && (
          <StyledLabel
            htmlFor={name}
            error={error}
            fontSize={countFontSize}
            fontWeight={countFontWeight}
          >
            {count ?? 0} / {maxCount}
          </StyledLabel>
        )}
        {!showCount && infoText && showInfoIcon && (
          <StyledLabel>
            <Tooltip
              text={infoText}
              isVisible
              tooltipStyle={{
                fontSize: 12,
                maxWidth: 240,
                whiteSpace: 'pre-wrap',
                lineBreak: 'normal',
              }}
              position="top-center"
              opacity="1"
              noArrow={false}
            >
              <StyledInfoIcon>
                <InfoIconGreen width={13} height={13} />
              </StyledInfoIcon>
            </Tooltip>
          </StyledLabel>
        )}
      </LabelRow>

      <InputWrapper style={extraWrapperStyle ? extraWrapperStyle : {}}>
        <StyledInput
          width={width}
          id={name}
          name={name}
          type={inputType}
          min={min}
          pattern={pattern}
          disabled={disabled}
          readOnly={readonly}
          placeholder={placeholder}
          error={error}
          ref={register}
          value={value}
          onChange={(e) => (onChange ? onChange(e) : null)}
          onBlur={onBlur}
          defaultValue={defaultValue}
          withShowIcon={withShowIcon}
          style={inputValueStyle}
        />
        {withSearchIcon && (
          <StyledIconsBlock>
            <StyledLeftIcon src={SearchIcon} alt="" />
          </StyledIconsBlock>
        )}
        {type === 'password' && withShowIcon ? (
          <ShowPasswordButton
            type="button"
            onClick={togglePasswordVisibility}
            tabIndex="-1"
          >
            {inputType === 'password' ? (
              <ShowPasswordIcon />
            ) : (
              <HidePasswordIcon />
            )}
          </ShowPasswordButton>
        ) : null}
      </InputWrapper>

      {error && (
        <StyledErrorMessage>
          {error.message || t(`errors.${error.type}`)}
        </StyledErrorMessage>
      )}
    </StyledWrapper>
  );
};

TextInput.propTypes = {
  name: string.isRequired,
  type: string,
  placeholder: string,
  register: func || MutableRefObject,
  disabled: bool,
  label: string,
  error: shape({
    message: string,
    type: string,
  }),
  width: string,
  value: string,
  onChange: func,
  defaultValue: string,
  withShowIcon: bool,
  extraWrapperStyle: React.CSSProperties,
  inputValueStyle: React.CSSProperties,
  count: number,
  maxCount: number,
  countFontSize: number,
  countFontWeight: number,
};

TextInput.defaultProps = {
  type: 'text',
  placeholder: '',
  register: noop,
  disabled: false,
  label: '',
  error: null,
  width: '',
  value: undefined,
  onChange: noop,
  defaultValue: null,
  withShowIcon: false,
  count: undefined,
  maxCount: undefined,
  countFontSize: 10,
  countFontWeight: 400,
};

export default TextInput;
